import React from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from '@/contexts/LanguageContext';
import { Slide } from '@/types';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

interface SortableSlideItemProps {
  slide: Slide;
  index: number;
  isActive: boolean;
  batchMode: boolean;
  isSelected: boolean;
  generationStatus?: 'idle' | 'generating' | 'success' | 'error';
  isGenerating: boolean;
  onSelect: () => void;
  onToggleSelection: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

interface SlideListPanelProps {
  slides: Slide[];
  activeSlideId: string;
  batchMode: boolean;
  selectedSlideIds: Set<string>;
  generatingSlideIds: Set<string>;
  slideGenerationStatus: Record<string, 'idle' | 'generating' | 'success' | 'error'>;
  batchGenerating: boolean;
  batchGeneratingLabel: string;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
  onSlideSelect: (slideId: string) => void;
  onToggleSelection: (slideId: string) => void;
  onDeleteSlide: (slideId: string) => void;
  onDuplicateSlide: (slideId: string) => void;
  onAddSlide: () => void;
  onToggleBatchMode: () => void;
  onToggleSelectAll: () => void;
  onBatchGenerateImages: () => void;
  SortableSlideItem: React.ComponentType<SortableSlideItemProps>;
}

const SlideListPanel: React.FC<SlideListPanelProps> = ({
  slides,
  activeSlideId,
  batchMode,
  selectedSlideIds,
  generatingSlideIds,
  slideGenerationStatus,
  batchGenerating,
  batchGeneratingLabel,
  sensors,
  onDragEnd,
  onSlideSelect,
  onToggleSelection,
  onDeleteSlide,
  onDuplicateSlide,
  onAddSlide,
  onToggleBatchMode,
  onToggleSelectAll,
  onBatchGenerateImages,
  SortableSlideItem,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-3 border-b border-border">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-sm uppercase tracking-wide text-muted-foreground">
            {t('workspace.slidesCount', { count: slides.length })}
          </h2>
          <div className="flex items-center gap-1">
            <Button
              data-tour="editor-batch-mode-mobile"
              variant={batchMode ? "default" : "ghost"}
              size="icon"
              onClick={onToggleBatchMode}
              disabled={batchGenerating}
              title={batchMode ? t('workspace.exitBatchMode') : t('workspace.batchGenerate')}
              className="h-8 w-8"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {batchMode && (
          <div className="p-2 bg-secondary rounded-lg mb-2 space-y-2">
            <div className="flex items-center justify-between">
              <Button variant="link" size="sm" onClick={onToggleSelectAll} className="text-xs text-primary p-0 h-auto">
                {selectedSlideIds.size === slides.length ? t('workspace.deselectAll') : t('workspace.selectAll')}
              </Button>
              <span className="text-xs text-muted-foreground">{selectedSlideIds.size} selected</span>
            </div>
            <Button onClick={onBatchGenerateImages} disabled={selectedSlideIds.size === 0 || batchGenerating} className="w-full text-xs" size="sm">
              {batchGenerating ? <span aria-live="polite" aria-atomic="true">{batchGeneratingLabel}</span> : t('workspace.generateImages', { count: selectedSlideIds.size })}
            </Button>
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={slides.map(s => s.id)} strategy={verticalListSortingStrategy}>
            {slides.map((slide, idx) => (
              <SortableSlideItem
                key={slide.id}
                slide={slide}
                index={idx}
                isActive={activeSlideId === slide.id}
                batchMode={batchMode}
                isSelected={selectedSlideIds.has(slide.id)}
                generationStatus={slideGenerationStatus[slide.id]}
                isGenerating={generatingSlideIds.has(slide.id)}
                onSelect={() => onSlideSelect(slide.id)}
                onToggleSelection={() => onToggleSelection(slide.id)}
                onDelete={() => onDeleteSlide(slide.id)}
                onDuplicate={() => onDuplicateSlide(slide.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div className="p-3 border-t border-border">
        <Button data-tour="editor-add-slide-mobile" variant="outline" onClick={onAddSlide} className="w-full border-dashed text-muted-foreground hover:text-foreground">
          <Plus className="h-4 w-4 mr-2" />
          {t('workspace.addSlideFooter')}
        </Button>
      </div>
    </div>
  );
};

export default SlideListPanel;
